import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  BarChart3, Target, MapPin, Clock, Users, TrendingUp, Settings2, 
  AlertCircle, Loader2, ArrowRight, Zap, Eye, MousePointer, Store,
  Activity, Brain, Sparkles
} from "lucide-react";

export default function Attribution() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [summary, setSummary] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [campaignsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/attribution/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCampaigns(campaignsRes.data);
      setSummary(summaryRes.data);
      
      if (campaignsRes.data.length > 0) {
        await loadCampaignAttribution(campaignsRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load attribution data");
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignAttribution = async (campaignId) => {
    setSelectedCampaign(campaignId);
    try {
      const [attrRes, configRes] = await Promise.all([
        axios.get(`${API}/analytics/attribution/${campaignId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns/${campaignId}/attribution`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCampaignData(attrRes.data);
      setConfig(configRes.data);
    } catch (error) {
      toast.error("Failed to load campaign attribution");
    }
  };

  const saveConfig = async () => {
    if (!selectedCampaign || !config) return;
    setSavingConfig(true);
    try {
      await axios.put(`${API}/campaigns/${selectedCampaign}/attribution`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Attribution settings saved");
      setConfigDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // The API returns AWAITING_DATA with real zeros until trips generate metrics.
  const isAwaitingData = summary?.data_status === "AWAITING_DATA";

  return (
    <div className="space-y-6" data-testid="attribution-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            AI Targeting & Attribution
          </h1>
          <p className="text-muted-foreground mt-1">
            Track ad performance and measure real-world conversions
          </p>
        </div>
        {isAwaitingData && (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 gap-1">
            <Sparkles className="h-3 w-3" />
            Awaiting Data
          </Badge>
        )}
      </div>

      {/* Awaiting-data notice */}
      {isAwaitingData && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-amber-600">Awaiting Real Trip Data</p>
                <p className="text-sm text-muted-foreground mt-1">
                  No attribution data has been recorded yet, so the metrics below are all zero. They will populate automatically once trips begin and ad impressions are recorded.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Impressions</p>
                <p className="text-2xl font-bold mt-1">{summary?.total_impressions?.toLocaleString() || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Eye className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Clicks</p>
                <p className="text-2xl font-bold mt-1">{summary?.total_clicks?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">CTR: {summary?.overall_ctr || 0}%</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <MousePointer className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Store Visits</p>
                <p className="text-2xl font-bold mt-1">{summary?.total_conversions?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Conv: {summary?.overall_conversion_rate || 0}%</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Store className="h-5 w-5 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">AI Score Avg</p>
                <p className="text-2xl font-bold mt-1">--</p>
                <p className="text-xs text-muted-foreground mt-1">Pending ML data</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Brain className="h-5 w-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Breakdown */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" />
            Conversion Breakdown
          </CardTitle>
          <CardDescription>
            Store visits categorized by nearby and attribution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-sm font-medium">Direct To Door</span>
              </div>
              <p className="text-2xl font-bold">{summary?.by_conversion_type?.direct || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Dropped off at your location</p>
            </div>

            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
                <span className="text-sm font-medium">Nearby Influenced</span>
              </div>
              <p className="text-2xl font-bold">{summary?.by_conversion_type?.proximity || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Dropped off near your location</p>
            </div>

            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-purple-500" />
                <span className="text-sm font-medium">Prompted</span>
              </div>
              <p className="text-2xl font-bold">{summary?.by_conversion_type?.prompted || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Via CTA/notification</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign-Level Attribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign List */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg">Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
            {campaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No campaigns yet
              </p>
            ) : (
              campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  onClick={() => loadCampaignAttribution(campaign.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedCampaign === campaign.id
                      ? 'bg-primary/10 border border-primary'
                      : 'bg-secondary/30 border border-transparent hover:border-primary/50'
                  }`}
                  data-testid={`campaign-attr-${campaign.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{campaign.name}</span>
                    <Badge variant={campaign.status === 'ACTIVE' ? 'default' : 'secondary'} className="text-xs">
                      {campaign.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Campaign Details */}
        <Card className="glass lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">
                {campaigns.find(c => c.id === selectedCampaign)?.name || 'Select a campaign'}
              </CardTitle>
              <CardDescription>Attribution metrics and settings</CardDescription>
            </div>
            {selectedCampaign && (
              <Button variant="outline" size="sm" onClick={() => setConfigDialogOpen(true)}>
                <Settings2 className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!selectedCampaign ? (
              <div className="text-center py-8 text-muted-foreground">
                Select a campaign to view attribution data
              </div>
            ) : campaignData ? (
              <div className="space-y-6">
                {campaignData.data_status === "AWAITING_DATA" && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
                    <span className="text-amber-600 font-medium">No data yet</span>
                    <span className="text-muted-foreground"> - metrics will appear once trips begin</span>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold">{campaignData.impressions?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Impressions</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold">{campaignData.clicks?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Clicks</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold">{campaignData.conversions?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Conversions</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground">Click-Through Rate</p>
                    <p className="text-xl font-bold">{campaignData.ctr}%</p>
                  </div>
                  <div className="p-4 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    <p className="text-xl font-bold">{campaignData.conversion_rate}%</p>
                  </div>
                </div>

                {/* Conversion funnel */}
                <div>
                  <p className="text-sm font-medium mb-3">Conversion Funnel</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-8 bg-blue-500/20 rounded flex items-center justify-center text-xs">
                      {campaignData.impressions?.toLocaleString()} impressions
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 h-8 bg-green-500/20 rounded flex items-center justify-center text-xs">
                      {campaignData.clicks?.toLocaleString()} clicks
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 h-8 bg-purple-500/20 rounded flex items-center justify-center text-xs">
                      {campaignData.conversions?.attributed || 0} attributed
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Targeting Placeholder */}
      <Card className="glass border-dashed">
        <CardContent className="py-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">AI Targeting Engine</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Machine learning-powered ad targeting will be enabled once sufficient trip data is collected. 
              The system will optimise ad delivery based on passenger behaviour patterns, route predictions, and engagement history.
            </p>
            <div className="flex items-center justify-center gap-4 mt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-muted-foreground">0</p>
                <p className="text-xs text-muted-foreground">Training samples</p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="text-center">
                <p className="text-2xl font-bold text-muted-foreground">10K</p>
                <p className="text-xs text-muted-foreground">Required for ML</p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">0%</p>
                <p className="text-xs text-muted-foreground">Progress</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attribution Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attribution Settings</DialogTitle>
          </DialogHeader>
          
          {config && (
            <div className="space-y-6 py-4">
              {/* Attribution Windows */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Attribution Windows</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Impression Window (hours)</Label>
                    <Input
                      type="number"
                      value={config.impression_window_hours}
                      onChange={(e) => setConfig({...config, impression_window_hours: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">How long after viewing an ad a conversion can be attributed</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Click Window (hours)</Label>
                    <Input
                      type="number"
                      value={config.click_window_hours}
                      onChange={(e) => setConfig({...config, click_window_hours: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">How long after clicking an ad a conversion can be attributed</p>
                  </div>
                </div>
              </div>

              {/* Distance thresholds. Round 4, G-2 and decision D5 took every distance
                  out of merchant-facing copy, and these stay because this is the admin
                  panel where the figures are SET rather than quoted.

                  They are labelled honestly now, because tracing them answered the D5
                  question: nothing in the CRM reads either value. They appear on
                  AttributionConfig and in this form, and in no serving path, no billing
                  path and no attribution decision. proximity_radius_m defaulting to 250
                  is where Tier Pricing's "250m" label came from; MetricInfo's
                  "50 to 200m" matched neither and was simply invented.

                  So the passenger app decides which label a drop-off gets, exactly as
                  Robin read it, and these are figures the CRM records for it. Left in
                  place rather than removed: config the app may read over the API is not
                  something to delete in a copy round. */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Drop off distance thresholds (metres)</h4>
                <p className="text-xs text-muted-foreground">
                  Recorded here and applied by the Booma passenger app, which decides
                  from the drop-off itself which of the two a delivered customer counts
                  as. The CRM bills whichever it is told.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Direct To Door</Label>
                    <Input
                      type="number"
                      value={config.direct_radius_m}
                      onChange={(e) => setConfig({...config, direct_radius_m: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nearby Influenced</Label>
                    <Input
                      type="number"
                      value={config.proximity_radius_m}
                      onChange={(e) => setConfig({...config, proximity_radius_m: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
              </div>

              {/* Attribution Model */}
              <div className="space-y-2">
                <Label>Attribution Model</Label>
                <Select value={config.model} onValueChange={(v) => setConfig({...config, model: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LAST_TOUCH">Last Touch</SelectItem>
                    <SelectItem value="FIRST_TOUCH">First Touch</SelectItem>
                    <SelectItem value="LINEAR">Linear</SelectItem>
                    <SelectItem value="TIME_DECAY">Time Decay</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">How credit is assigned when multiple ads are seen before conversion</p>
              </div>

              {/* Tracking Toggles */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Conversion Tracking</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label>Track Direct</Label>
                    <Switch checked={config.track_direct} onCheckedChange={(v) => setConfig({...config, track_direct: v})} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Track Nearby</Label>
                    <Switch checked={config.track_proximity} onCheckedChange={(v) => setConfig({...config, track_proximity: v})} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Track Prompted</Label>
                    <Switch checked={config.track_prompted} onCheckedChange={(v) => setConfig({...config, track_prompted: v})} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveConfig} disabled={savingConfig}>
              {savingConfig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
